export interface FacetItem {
	value?: string | number
	count?: number | null
	label?: string | number
	slug?: string
}

type FilterValue = string | number | boolean | string[] | number[] | null

interface ProductCatalogState {
	activeFilters?: {
		conditions?: string | string[] | null
		fuelTypes?: string | string[] | null
		[key: string]: FilterValue | undefined
	}
	facets?: Record<string, FacetItem[]>
}

export function useVehicleFilters(productCatalog: {
	activeFilters: () => ProductCatalogState['activeFilters']
	facets: () => ProductCatalogState['facets']
}) {
	const { partnerName } = useGetPartnerName()
	const activeFilters = computed(productCatalog.activeFilters)
	const facets = computed(productCatalog.facets)

	// Active filter states
	const isConditionNew = computed(() => {
		const conditions = activeFilters.value?.conditions
		return Array.isArray(conditions)
			? conditions.includes('new')
			: conditions === 'new'
	})

	const isConditionUsed = computed(() => {
		const conditions = activeFilters.value?.conditions
		return Array.isArray(conditions)
			? conditions.includes('used')
			: conditions === 'used'
	})

	const isFuelTypeEv = computed(() => {
		const fuelTypes = activeFilters.value?.fuelTypes
		return Array.isArray(fuelTypes)
			? fuelTypes.includes('electric')
			: fuelTypes === 'electric'
	})

	const isConditionAll = computed(() => {
		if (!partnerName.value) {
			return !isConditionNew.value && !isConditionUsed.value
		} else {
			return (
				!isConditionNew.value &&
				!isConditionUsed.value &&
				!isFuelTypeEv.value
			)
		}
	})

	// Facet counts
	const facetCondition = computed<FacetItem[]>(() => {
		return facets.value?.['conditions'] ?? []
	})

	const facetFuelTypes = computed<FacetItem[]>(() => {
		return facets.value?.['fuelTypes'] ?? []
	})

	const totalNewVehiclesCount = computed<number>(() => {
		const newItem = facetCondition.value.find(
			(item) => item.value === 'new'
		)
		return newItem ? (newItem.count ?? 0) : 0
	})

	const totalUsedVehiclesCount = computed<number>(() => {
		const usedItem = facetCondition.value.find(
			(item) => item.value === 'used'
		)
		return usedItem ? (usedItem.count ?? 0) : 0
	})

	const totalEvVehiclesCount = computed<number>(() => {
		const fuelTypeItem = facetFuelTypes.value.find(
			(item) => item.value === 'electric'
		)
		return fuelTypeItem ? (fuelTypeItem.count ?? 0) : 0
	})

	// Toggle button labels
	const totalNewVehiclesCountLabel = computed(
		() => `${totalNewVehiclesCount.value} cars`
	)
	const totalUsedVehiclesCountLabel = computed(
		() => `${totalUsedVehiclesCount.value} cars`
	)
	const totalEvCountLabel = computed(
		() => `${totalEvVehiclesCount.value} cars`
	)
	const totalVehiclesCountLabel = computed(() => {
		return `${totalNewVehiclesCount.value + totalUsedVehiclesCount.value} cars`
	})

	return {
		isConditionNew,
		isConditionUsed,
		isFuelTypeEv,
		isConditionAll,
		totalNewVehiclesCount,
		totalUsedVehiclesCount,
		totalEvVehiclesCount,
		totalNewVehiclesCountLabel,
		totalUsedVehiclesCountLabel,
		totalEvCountLabel,
		totalVehiclesCountLabel,
	}
}
